import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Size options for sidebar icons.
 * - sm: 1rem (w-4 h-4)
 * - md: 1.125rem — the navigation items' size
 */
export type SidebarIconSize = "sm" | "md";

interface SidebarIconProps {
  /** Lucide icon component to render */
  icon: LucideIcon;
  /** Icon size - sm (1rem) or md (1.125rem). Defaults to md. */
  size?: SidebarIconSize;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses: Record<SidebarIconSize, string> = {
  sm: "h-4 w-4",
  md: "h-[1.125rem] w-[1.125rem]",
};

/**
 * SidebarIcon - Atom component
 *
 * Renders Lucide icons consistently across the navigation sidebar.
 * Part of the atomic design system for NavigationSidebar.
 *
 * @example
 * ```tsx
 * import { Home } from 'lucide-react';
 * <SidebarIcon icon={Home} />
 * <SidebarIcon icon={Home} size="sm" />
 * <SidebarIcon icon={Home} size="md" className="text-blue-500" />
 * ```
 */
export function SidebarIcon({ icon: Icon, size = "md", className }: SidebarIconProps) {
  return <Icon className={cn(sizeClasses[size], className)} />;
}
